const SEEN_KEYS_STORAGE_KEY = '__sentry_replay_seen_keys';

let inMemorySeenKeys = new Set();

const getSessionStorage = () => {
    try {
        return globalThis.sessionStorage;
    } catch {
        return;
    }
};

export const loadSeenKeys = () => {
    const storage = getSessionStorage();
    if (!storage) {
        return inMemorySeenKeys;
    }
    try {
        const raw = storage.getItem(SEEN_KEYS_STORAGE_KEY);
        return new Set(raw ? JSON.parse(raw) : []);
    } catch {
        return inMemorySeenKeys;
    }
};

export const persistSeenKeys = (keys) => {
    const storage = getSessionStorage();
    if (!storage) {
        inMemorySeenKeys = keys;
        return;
    }
    try {
        storage.setItem(SEEN_KEYS_STORAGE_KEY, JSON.stringify([...keys]));
    } catch {
        inMemorySeenKeys = keys;
    }
};

export const replayErrorSignature = (event) => {
    const exceptionValue = event.exception?.values?.[0];
    const type = exceptionValue?.type || event.message || 'unknown';
    const topFrame = exceptionValue?.stacktrace?.frames?.at?.(-1);
    return `${type}|${topFrame?.function || ''}|${topFrame?.filename || ''}`;
};

const NON_ACTIONABLE_REPLAY_ERROR_PATTERNS = [
    /failed to fetch/i,
    /networkerror/i,
    /network request failed/i,
    /load failed/i,
    /content security policy/i,
    /refused to connect/i,
    /aborterror/i,
];

export const isNonActionableReplayError = (event) => {
    const exception = event?.exception?.values?.[0];
    const haystack = [
        exception?.type,
        exception?.value,
        event?.message,
    ].filter(Boolean).join(' ');

    return NON_ACTIONABLE_REPLAY_ERROR_PATTERNS.some((pattern) => pattern.test(haystack));
};

export const beforeErrorSampling = (event) => {
    const level = event.level || 'error';
    if (!['fatal', 'error'].includes(level)) {
        return false;
    }
    if (isNonActionableReplayError(event)) {
        return false;
    }

    const path = globalThis.location?.pathname || 'unknown';
    const key = `${path}::${replayErrorSignature(event)}`;

    const seenKeys = loadSeenKeys();
    if (seenKeys.has(key)) {
        return false;
    }

    seenKeys.add(key);
    persistSeenKeys(seenKeys);
    return true;
};

/**
 * The message each browser gives a `fetch` that never got a response: offline, blocked
 * by an extension, aborted by navigation, or a CORS preflight answered by something other
 * than our API (e.g. a preview-router 502). Chrome, Firefox and Safari respectively.
 * The SDK's fetch instrumentation appends the request host, e.g. `Failed to fetch (api.lodgr.co)`.
 * Anchored so `Failed to fetch dynamically imported module` — a stale-deploy chunk error
 * we do want — is not swallowed with them.
 */
const NETWORK_FAILURE_MESSAGES = [
    /^failed to fetch(?: \((?<host>[^)]+)\))?$/i,
    /^networkerror when attempting to fetch resource\.(?: \((?<host>[^)]+)\))?$/i,
    /^load failed(?: \((?<host>[^)]+)\))?$/i,
];

/**
 * API rejections that are the expected answer for a signed-out or under-privileged visitor.
 * Noise outside production; in production a spike of them is a broken auth flow or a client
 * bug, so they are kept at warning level and grouped by status and API error code.
 */
const AUTH_REJECTION_STATUSES = new Set([
    401,
    403,
]);

/** `VITE_SENTRY_ENVIRONMENT_NAME` of the production build — the only one that keeps 401/403. */
const PRODUCTION_SENTRY_ENVIRONMENT = 'production';

/** Fingerprint prefix that groups every kept 401/403 apart from the error it was thrown in. */
const API_CLIENT_ERROR_FINGERPRINT = 'api-client-error';

/** A stable API rule identity: letters and underscores only, at most 64 characters. */
const API_ERROR_CODE_PATTERN = /^[A-Z_a-z]{1,64}$/;

/** Fingerprint and tag value for a 401/403 whose API body carried no `error_code`. */
const MISSING_ERROR_CODE = 'none';

/** A UUID path segment, e.g. a booking or profile id. */
const UUID_SEGMENT_PATTERN = /^[\dA-Fa-f]{8}(?:-[\dA-Fa-f]{4}){3}-[\dA-Fa-f]{12}$/;

/** An all-digit path segment: a numeric id. */
const NUMERIC_SEGMENT_PATTERN = /^\d+$/;

/**
 * Any other segment holding a digit: hex digests, opaque tokens, referral codes, numbered slugs.
 * No static API route segment contains a digit, so this only ever catches a value.
 */
const DIGIT_BEARING_SEGMENT_PATTERN = /\d/;

/** A segment this long is a value (a token, a slug), never a route name. */
const MAX_ROUTE_SEGMENT_LENGTH = 32;

/** The path part of a request target: everything before a query string or fragment. */
const REQUEST_PATH_PATTERN = /^(?<path>[^#?]*)/;

const API_ENDPOINT_PLACEHOLDERS = {
    id: ':id',
    slug: ':slug',
    token: ':token',
    uuid: ':uuid',
};

/**
 * Route names whose next segment is always a slug. An organization slug can be digit-free and
 * short, so the digit and length rules alone would leave a customer's slug in the tag.
 */
const SLUG_PARENT_SEGMENTS = new Set(['organization']);

/** One path segment, or its placeholder when it is a value rather than a route name. */
const normaliseApiPathSegment = (segment, previousSegment) => {
    if (SLUG_PARENT_SEGMENTS.has(previousSegment)) {
        return API_ENDPOINT_PLACEHOLDERS.slug;
    }
    if (UUID_SEGMENT_PATTERN.test(segment)) {
        return API_ENDPOINT_PLACEHOLDERS.uuid;
    }
    if (NUMERIC_SEGMENT_PATTERN.test(segment)) {
        return API_ENDPOINT_PLACEHOLDERS.id;
    }
    if (DIGIT_BEARING_SEGMENT_PATTERN.test(segment) || segment.length > MAX_ROUTE_SEGMENT_LENGTH) {
        return API_ENDPOINT_PLACEHOLDERS.token;
    }
    return segment;
};

/**
 * The API route a rejection came from, with ids replaced by placeholders and the query string
 * dropped (`/booking/:id/pay`), so the tag has one value per route rather than per record.
 */
export const normaliseApiEndpoint = (requestPath) => requestPath
    .match(REQUEST_PATH_PATTERN)
    .groups
    .path
    .split('/')
    .map((segment, index, segments) => normaliseApiPathSegment(segment, segments[index - 1]))
    .join('/');

/** How far down `error.cause` to look; wrappers nest one or two deep in practice. */
const MAX_CAUSE_DEPTH = 5;

/**
 * Third-party errors reported through our page. `Java object is gone` is an Android
 * in-app browser (WebView) bridge torn down mid-call, never our code.
 */
const THIRD_PARTY_ERROR_MESSAGES = [/Error invoking postMessage: Java object is gone/];

const isNetworkFailureMessage = (type, message) => type === 'TypeError'
    && typeof message === 'string'
    && NETWORK_FAILURE_MESSAGES.some((pattern) => pattern.test(message.trim()));

/** The thrown error followed by its `cause` chain, so a service that re-wraps keeps its origin. */
const causeChain = (error) => {
    const chain = [];
    let current = error;
    while (current && typeof current === 'object' && chain.length < MAX_CAUSE_DEPTH) {
        chain.push(current);
        current = current.cause;
    }
    return chain;
};

export const isBrowserNetworkFailure = (event, hint) => {
    const exception = event?.exception?.values?.[0];
    if (isNetworkFailureMessage(exception?.type, exception?.value)) {
        return true;
    }
    return causeChain(hint?.originalException)
        .some((error) => isNetworkFailureMessage(error.name, error.message));
};

/** The first error in the `cause` chain that is a 401/403 API rejection, if any. */
const findAuthRejection = (hint) => causeChain(hint?.originalException)
    .find((error) => AUTH_REJECTION_STATUSES.has(Number(error.statusCode)));

/**
 * The API's stable rule identity for a rejection. `fetchResponseHandler` stores the whole
 * error body as `error.data` (the API envelope has no `data` key), and the API emits the
 * identity as both `code` and `error_code` with identical values; `error_code` is the spelling
 * the frontend keys off. It becomes a fingerprint and a tag, so anything that is not a plain
 * snake_case identifier (an id, a sentence) collapses to `none` rather than minting new issues.
 */
const apiErrorCode = (error) => {
    const errorCode = error.data?.error_code;
    return typeof errorCode === 'string' && API_ERROR_CODE_PATTERN.test(errorCode) ? errorCode : MISSING_ERROR_CODE;
};

/** Keep a production 401/403 as a warning, grouped by status and API error code, tagged by endpoint. */
const markAuthRejection = (event, error) => {
    const status = String(Number(error.statusCode));
    const errorCode = apiErrorCode(error);
    event.level = 'warning';
    event.fingerprint = [
        API_CLIENT_ERROR_FINGERPRINT,
        status,
        errorCode,
    ];
    event.tags = {
        ...event.tags,
        // Not in the fingerprint: the issue stays one per status and code, and the tag shows
        // which endpoints it spans. Errors not thrown by `fetchResponseHandler` carry no path.
        ...(typeof error.requestPath === 'string' && {
            api_endpoint: normaliseApiEndpoint(error.requestPath),
        }),
        error_code: errorCode,
        http_status: status,
    };
    return event;
};

/**
 * Build the `beforeSend` for one Sentry environment. Browser-level fetch failures are dropped
 * everywhere: they are not bugs in our code, and a dead API is caught by the Sentry uptime
 * monitor on api.lodgr.co/status, not by every browser that failed to reach it. The 401/403
 * `fetchResponseHandler` throws — including when a service re-throws it as the `cause` of its
 * own error — is dropped outside production and kept as a grouped warning in production.
 *
 * @param {{environment?: string}} options — `environment` is the Sentry environment name
 */
export const buildBeforeSend = ({
    environment,
} = {}) => {
    const keepsAuthRejections = environment === PRODUCTION_SENTRY_ENVIRONMENT;
    return (event, hint) => {
        if (isBrowserNetworkFailure(event, hint)) {
            // eslint-disable-next-line unicorn/no-null -- Sentry drops an event only on an explicit null
            return null;
        }
        const authRejection = findAuthRejection(hint);
        if (!authRejection) {
            return event;
        }
        if (!keepsAuthRejections) {
            // eslint-disable-next-line unicorn/no-null -- Sentry drops an event only on an explicit null
            return null;
        }
        return markAuthRejection(event, authRejection);
    };
};

/** The non-production `beforeSend`: drops fetch failures and 401/403 alike. */
export const beforeSend = buildBeforeSend();

export const buildSentryOptions = (environment, sentry) => {
    const replayOptions = {
        beforeErrorSampling,
        blockAllMedia: false,
        maskAllInputs: false,
        maskAllText: false,
    };

    // TODO(sentry-replay-rollout): re-enable prod-only gate before merge to prod
    // if (!environment.isProd) return null  // or skip replay integration
    const browserTracing = sentry.browserTracingIntegration();
    const replay = sentry.replayIntegration(replayOptions);

    return {
        beforeSend: buildBeforeSend({
            environment: environment.environment,
        }),
        dsn: environment.dsn,
        environment: environment.environment,
        ignoreErrors: THIRD_PARTY_ERROR_MESSAGES,
        integrations: [browserTracing, replay],
        // TODO(sentry-replay-rollout): keep replaysSessionSampleRate at 0 for cost; set 1.0 only for "see any replay" smoke tests
        replaysOnErrorSampleRate: 1,
        replaysSessionSampleRate: 0,
    };
};
